<template>
	<div>

        <h2>
        {{ $t('edit-report-data-element') }}
        <div class="float-right">
            <button @click="save" class="btn btn-primary mr-2">{{ $t('save') }}</button>
            <button @click="cancel" class="btn btn-secondary">{{ $t('cancel') }}</button>
        </div>
        </h2>

        <div v-if="saved">
            <div class="alert alert-success mt-8">{{ $t('data-element-saved') }}</div>
        </div>
        <div v-else-if="showSaveFailedAlert">
            <div class="alert alert-danger mt-8">{{ $t('save-failed') }}</div>
        </div>

         <b-tabs content-class="mt-3">
            <b-tab :title="$t('settings')" active>
                <div class="row">

                    <div class="col auto" v-if="isCustomElement">
                        <div class="alert alert-info">
                            {{ $t('custom-data-element-settings-cannot-be-edited') }}
                        </div>
                    </div>

                    <template v-else>
                        <div class="col">
                            <fieldset >
                                <legend>{{ $t('settings') }}</legend>
                                <div class="form-group">
                                    <label for="dataelement-name">{{ $t('data-element-name') }}</label>
                                    <input id="dataelement-name" name="dataelementname" class="form-control" v-model="dataelement.name" :placeholder="$t('data-element-name')" required>
                                </div>
                                <div class="form-group">
                                    <label for="dataelement-caption">{{ $t('caption') }}</label>
                                    <input id="dataelement-caption" name="dataelementcaption" class="form-control" v-model="dataelement.caption" :placeholder="$t('caption')" required>
                                </div>
                                <div class="form-group">
                                    <label>{{ $t('data-source') }}</label>
                                    <select class="form-control" v-model="datasourceselect" @change="dataSourceChange">
                                        <option v-for="(caption, name) in datasources" :key="name" :value="name">
                                            {{ $t(caption) }}
                                        </option>
                                    </select>
                                    <input class="form-control mt-2" v-model="dataelement.data.datasource" v-if="datasourceselectother" @blur="updateReturnProperties">
                                </div>
                            </fieldset>
                        </div>
                        <div class="col">
                            <fieldset >
                                <legend>{{ $t('filters') }}</legend>
                                <DataElementFilters
                                    :dataelement="dataelement"
                                    :datasource="dataelement.data.datasource"
                                    :filterDataReady="filterDataReady"
                                    :procedures="procedures"
                                    :grades="grades"
                                    :competencyTests="competencyTests"
                                    :clinics="clinics"
                                    :specialties="specialties"
                                    :forms="forms"
                                    :formFields="formFields"
                                    @updateReturnProperties="updateReturnProperties"
                                    ref="dataelementfilters"
                                />
                            </fieldset>
                        </div>
                        <div class="col">
                            <fieldset >
                                <legend>{{ $t('return') }}</legend>
                                <template v-if="editingProperty">
                                    <MathFormulaInput
                                        class="mb-2"
                                        v-model="editingProperty.value"
                                        :index="mathReferenceIndex"
                                        :self="selfReference"
                                        textareaStyle="height: 300px;"
                                    />
                                    <button class="btn btn-secondary" @click="finishedEditingCustomProperty">OK</button>
                                </template>
                                <template v-else>
                                    <div class="btn-group block">
                                        <a class="btn" :class="{ 'btn-light': isCustomReturn, 'btn-success': !isCustomReturn }" @click="userSelectedCustomReturn = false; userSelectedDefaultReturn = true;">Default</a>
                                        <a class="btn" :class="{ 'btn-light': !isCustomReturn, 'btn-success': isCustomReturn }" @click="userSelectedCustomReturn = true; userSelectedDefaultReturn = false;">Custom</a>
                                    </div>
                                    <template v-if="isCustomReturn">
                                        <ul class="list-unstyled">
                                            <!-- iterate return properties -->
                                            <li v-for="(property, i) in returnProperties" :key="i">
                                                <b-form-checkbox
                                                v-model="property.selected"
                                                >
                                                {{ property.name }}
                                                <b-icon
                                                    v-if="property.value && customPropertyHasWarnings(property)"
                                                    icon="exclamation-triangle"
                                                    variant="warning"
                                                    v-b-tooltip.hover
                                                    :title="$t('math-formula-has-warnings')"
                                                ></b-icon>
                                                <template v-if="property.value">
                                                    [<a href="#" @click.prevent="viewEditCustomProperty(property)">{{ $t('view-edit') }}</a>]
                                                </template>
                                                </b-form-checkbox>
                                            </li>
                                        </ul>
                                        <p class="text-error mb-2" v-if="customPropertyError">{{ customPropertyError }}</p>
                                        <MathFormulaInput
                                            class="mb-2"
                                            v-model="customProperty"
                                            :index="mathReferenceIndex"
                                            :self="selfReference"
                                            :existingNames="returnPropertyNames"
                                            placeholder="Custom property e.g. two=MATH(${element.property}+1)"
                                        />
                                        <button class="btn btn-secondary" @click="addCustomProperty">{{ $t('add') }}</button>
                                    </template>
                                </template>
                            </fieldset>
                        </div>
                    </template>
                </div>
            </b-tab>
            <b-tab :title="$t('targets')" @click="updateOriginalDataElement" v-if="canHaveTargets">
                <DataElementTargets
                :targetsData="dataelement.targets"
                :selectedProperties="selectedReturnProperties"
                ref="dataelementtargets"
                :dataelement="dataelement"
                />
            </b-tab>
            <b-tab :title="$t('preview')" lazy @click="updateOriginalDataElement" v-if="canPreview">
                <DataElementPreview
                	:dataelement="dataelement"
                	:dataelementfilters="$refs.dataelementfilters"
                	:selectedReturnProperties="selectedReturnProperties"
                />
            </b-tab>
        </b-tabs>



	</div>
</template>

<script>
import DataElementFilters from "src/components/DataElement/DataElementFilters";
import DataElementTargets from "src/components/DataElement/DataElementTargets";
import DataElementPreview from "src/components/DataElement/DataElementPreview";
import MathFormulaInput from "src/components/DataElement/MathFormulaInput";
import { buildIndex, validateCustomProperty } from "src/include/mathreferences";
import datasources from "src/include/datasources";
export default {
	components: {
        DataElementFilters,
        DataElementTargets,
        DataElementPreview,
        MathFormulaInput
	},
    data() {
        return {
            dataelement_id: null,
            dataelement: {
                name: null,
                caption: null,
                data: {
                    datasource: 'none',
					date_range_from: null,
					date_range_length: null,
					date_range_denominator: 'MONTH',
                    grades: [],
                    params: [],
                    targets: [],
                    ttsessiontypes: [],
                    not_ttsessiontypes: [],
                },
                id: null,
                targets: []

            },
            datasources,
            datasourceselect: null,
            userSelectedCustomReturn: false,
            userSelectedDefaultReturn: false,
            procedures: [],
            grades: [],
            competencyTests: [],
            clinics: [],
            specialties: [],
            forms: [],
            formFields: [],
            filterDataReady: false,
            customProperty: null,
            customPropertyError: null,
            returnProperties: [],
            referenceDataelements: [],
            editingProperty: null,
            saved: false,
            showSaveFailedAlert: false
        }
    },
    watch: {
        customProperty(newVal) {
            if(newVal) {
                this.customPropertyError = null;
            }
        }
    },
    methods: {
        dataSourceChange() {
            // /portal/reports/alldata/datasourcereturnpropertylist/
            this.dataelement.data.datasource = this.datasourceselect;
			this.updateReturnProperties();
        },
        updateReturnProperties() {
        	this.returnProperties = [];
        	this.$cfetch(`${this.$settings.APIBaseURL}/reporting/alldata/datasourcereturnpropertylist/${this.dataelement.data.datasource}`, {
            	method: 'post',
            	data: {
  					count: this.$refs.dataelementfilters.getCount() ,
  					params: this.dataelement.data.params
  				}
            })
            .then(json => {

                for(const propertyName of json.fieldnames) {
                    this.addReturnProperty(
                        propertyName, // name
                        null, // no value - not custom
                        this.returnPropertyExistsInDataElement(propertyName) // selected?
                    );
                }

                // custom properties
                if(this.dataelement.data.return) {
                    for(const property of this.dataelement.data.return) {
                        if(!this.getReturnPropertyByName(property)) {
                            this.addReturnProperty(
                                this.getPropertyNameFromValue(property), // name
                                property, // value
                                true // custom properties always selected
                            );
                        }
                    }
                }

            }).catch(error => {
                this.$log.error.call(this, error);
            });
        },
        finishedEditingCustomProperty() {
            this.editingProperty = null;
        },
        viewEditCustomProperty(property) {
            this.editingProperty = property;
        },
        returnPropertyExistsInDataElement(name) {
            if(Array.isArray(this.dataelement.data.return)) {
                for(const property of this.dataelement.data.return) {
                    if(property == name) {
                        return true;
                    }
                }
            } else {
                return true; // not array
            }
            return false;
        },
        getReturnPropertyByName(name) {
            for(const property of this.returnProperties) {
                if(property.name == name) {
                    return property;
                }
            }
            return null;
        },
        deselectReturnPropertyByName(name) {
            for(const property of this.returnProperties) {
                if(property.name == name) {
                    property.selected = false;
                }
            }
        },
        addCustomProperty() {
            if (this.customProperty) {
                this.addReturnProperty(this.customPropertyName, this.customProperty, true)
                this.customProperty = null;
            } else {
                this.customPropertyError = 'Custom property must not be empty.';
            }
        },
        addReturnProperty(name, value, selected) {
            //this.deselectReturnPropertyByName(name);
            if(!this.getReturnPropertyByName(name)){
				this.returnProperties.push({
					name,
					value,
					selected
				});
            }
        },
		getGrades() {

			this.grades = [];
			var assessmentfields = this.$config.getValue("assessmentfields");
			for(var sectionid in assessmentfields){
				assessmentfields[sectionid].forEach(grade => {
					//For the time being, only grades are supported
					//if(grade.type == 'grade' || grade.type == 'oknotok'){
					if(grade.type == 'grade'){
						var newgrade = JSON.parse(JSON.stringify(grade));
						newgrade.proceduregrade = false;
						this.grades.push(newgrade);
					}
				});
			};
			var procedureassessmentfields = this.$config.getValue("proceduremarking");
			if(procedureassessmentfields && 'fields' in procedureassessmentfields){
				procedureassessmentfields.fields.forEach(grade => {
					//For the time being, only grades are supported
					//if(grade.type == 'grade' || grade.type == 'oknotok'){
					if(grade.type == 'grade'){
						var newgrade = JSON.parse(JSON.stringify(grade));
						newgrade.proceduregrade = true;
						this.grades.push(newgrade);
					}
				});
			};
		},
        getClinics() {
            this.$cfetch(`${this.$settings.APIBaseURL}/reporting/alldata/cliniclist`)
            .then(json => {
                this.clinics = json;
            }).catch(error => {
                this.$log.error.call(this, error);
            });
        },
        getSpecialties() {
            this.$cfetch(`${this.$settings.APIBaseURL}/reporting/alldata/specialtylist`)
            .then(json => {
                this.specialties = json;
            }).catch(error => {
                this.$log.error.call(this, error);
            });
        },
        saveFailed() {
            this.showSaveFailedAlert = true;
            setTimeout(() => {
                this.showSaveFailedAlert = false;
            }, 5000);
        },
        updateOriginalDataElement() {
            this.dataelement.data.count = this.$refs.dataelementfilters.getCount();
            this.dataelement.data.gradefield = this.$refs.dataelementfilters.getGradeField();
            this.dataelement.data.gradepassvalue = this.$refs.dataelementfilters.getGradePassValue();
            this.dataelement.data.params = this.$refs.dataelementfilters.getParams();
            this.dataelement.data.divisor = this.$refs.dataelementfilters.getDivisor();
        },
        save() {

            this.updateOriginalDataElement();

            const data = this.dataelement;
            data.save = 1;

            if(this.isCustomReturn) {
                data.data.return = this.selectedReturnProperties;
            } else {
            	data.data.return = null;
            }

            let url = `${this.$settings.APIBaseURL}/reporting/dataelements/savedataelement`;
            if(this.dataelement_id) {
                url = `${url}/${this.dataelement_id}`;
            }

            this.$cfetch(url, {
                method: "post",
                data
            })
            .then(json => {

                if (json.status == 'SUCCESS') {

                    this.dataelement_id = json.dataelement.id;
                    if(this.canHaveTargets){
                    	this.$refs.dataelementtargets.save(this.dataelement_id);
                    }

                    //this.dataelement = json.dataelement;

                    if(this.trySendMessageToParent('cafs.reportdataelementeditor.done')) {
                        return;
                    }

                    this.saved = true;
                    setTimeout(() => {
                        this.saved = false;
                    }, 5000);

                } else {
                    this.saveFailed();
                }

            }).catch(error => {
                this.saveFailed();
                this.$log.error.call(this, error);
            });



        },
        cancel() {
            if(this.trySendMessageToParent('cafs.reportdataelementeditor.done')) {
                return;
            }
            this.$router.push('/settings/reports/dataelements');
        },
        trySendMessageToParent(event) {
            if(window.opener) {
                return window.opener.postMessage(event, "*");
            }
            return false;
        },
        getDataElement(id) {
            return this.$cfetch(`${this.$settings.APIBaseURL}/reporting/dataelements/getdataelement/${id}`)
            .then(json => {
                this.$store.commit("hideBlockingSpinner");
                if (json.status == 'SUCCESS') {

                	if(!('grades' in json.dataelement.data)){
                		json.dataelement.data.grades = [];
                	}
					if(!('ttsessiontypes' in json.dataelement.data)){
            			json.dataelement.data.ttsessiontypes = [];
            		}
					if(!('not_ttsessiontypes' in json.dataelement.data)){
            			json.dataelement.data.not_ttsessiontypes = [];
            		}

            		if(!('date_range_from' in json.dataelement.data)){
            			json.dataelement.data.date_range_from = null;
            		}
					if(!('date_range_from' in json.dataelement.data)){
            			json.dataelement.data.date_range_length = null;
					}
					if(!('date_range_denominator' in json.dataelement.data)){
            			json.dataelement.data.date_range_denominator = 'MONTH';
            		}

                    this.dataelement = json.dataelement;
                    this.dataelement_id = json.dataelement.id;
                    if(this.dataelement.data.datasource in this.datasources){
                    	this.datasourceselect = this.dataelement.data.datasource;
						this.dataSourceChange();
                    } else {
                    	this.datasourceselect = 'other';
                    	this.updateReturnProperties();
                    }

                }
            }).catch(error => {
                this.$log.error.call(this, error);
                this.$store.commit("hideBlockingSpinner");
                this.$log.error.call(this, error);
            });
        },
        getData(propName, endpoint) {
            this.$cfetch(`${this.$settings.APIBaseURL}${endpoint}`)
            .then(json => {
                this[propName] = json;
            }).catch(error => {
                this.$log.error.call(this, error);
            });
        },
        getForms() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/forms/listall`)
            .then(json => {
                this.forms = json.results;
            }).catch(error => {
                this.$log.error.call(this, error);
            });
        },
        getFormFields() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/reporting/dataelements/forms/listresponses`)
            .then(json => {
                this.formFields = json.data;
            }).catch(error => {
                this.$log.error.call(this, error);
            });
        },
        getReferenceDataelements() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/reporting/dataelements/propertyreference`)
            .then(json => {
                this.referenceDataelements = json.dataelements || [];
            }).catch(error => {
                this.$log.error.call(this, error);
            });
        },
        customPropertyHasWarnings(property) {
            return validateCustomProperty(property.value, this.mathReferenceIndex, { self: this.selfReference }).length > 0;
        },
        getPropertyNameFromValue(value) {
            const parts = value.split('=');
            return parts[0];
        },
    },
    computed: {
        isCustomElement() {
            return this.dataelement && this.dataelement.custom == '1';
        },
        isCustomReturn() {
            return ((this.dataelement.data.return && Array.isArray(this.dataelement.data.return) && this.dataelement.data.return.length) || (this.userSelectedCustomReturn)) && !this.userSelectedDefaultReturn;
        },
        selectedReturnProperties() {
            const selected = [];
            for(const property of this.returnProperties) {
                if(property.selected) {
                    selected.push(property.value ? property.value : property.name);
                }
            }
            return selected;
        },
        returnPropertyNames() {
            return this.returnProperties.map(property => property.name);
        },
        // What this element will expose at runtime: only the selected properties with a custom return
        selfReference() {
            const properties = this.isCustomReturn
                ? this.returnProperties.filter(property => property.selected)
                : this.returnProperties;
            return {
                name: this.dataelement.name,
                caption: this.dataelement.caption,
                properties: properties.map(property => property.name)
            };
        },
        mathReferenceIndex() {
            return buildIndex(this.referenceDataelements, this.selfReference);
        },
        customPropertyName() {
            return this.getPropertyNameFromValue(this.customProperty);
        },
        datasourceselectother() {
        	if(!this.dataelement.data.datasource){
        		return false
        	}
        	if(this.dataelement.data.datasource == 'other'){
        		return true;
        	}
        	if(this.dataelement.data.datasource in this.datasources){
        		return false;
        	}
        	return true;
        },
        canHaveTargets() {
        	return [
        			'none',
        			'formelement',
                	'procedure',
                	'toothchart',
                	'radiograph',
                	'treatmentsession',
                	'procedures',
                	'grades',
                	'teachergrades',
                	'competencytestpass',
                	'form',
                    'formscores',
                	'absences',
                	'attendance',
                	'allstudentsreport',
                	'treatmentsessionreflections',
                	'treatmentsessionfeedback',
                	'treatmentsessionteacherfeedback',
                	'thirdpartydata',
                    'learningoutcomemapping',
                    'learningoutcomemapped',
                    'timeline',
                	'bristoldirectrestorations'
        		].includes(this.dataelement.data.datasource)
        },
        canPreview() {
        	return [
        			'formelement',
                	'procedure',
                	'toothchart',
                	'radiograph',
                	'treatmentsession',
                	'procedures',
                	'grades',
                	'teachergrades',
                	'students',
                	'competencytestpass',
                	'form',
                	'formscores',
                	'absences',
                	'attendance',
                	'allstudentsreport',
                	'treatmentsessionreflections',
                	'treatmentsessionfeedback',
                	'treatmentsessionteacherfeedback',
                	'thirdpartydata',
                    'learningoutcomemapping',
                    'learningoutcomemapped',
                    'timeline',
                	'bristoldirectrestorations'
        		].includes(this.dataelement.data.datasource)
        },
    },
    mounted() {

        this.$store.commit("showBlockingSpinner");

		this.getGrades();

        const data = {
            // propName: endpoint
            procedures: '/reporting/alldata/procedurelist',
            competencyTests: '/reporting/alldata/competencytestlist',
            specialties: '/reporting/alldata/specialtylist',
            clinics: '/reporting/alldata/cliniclist'
        };

        const promises = [];

        for(const [propName, endpoint] of Object.entries(data)) {
            promises.push(this.getData(propName, endpoint));
        }

        promises.push(this.getForms());
        promises.push(this.getFormFields());
        promises.push(this.getReferenceDataelements());

        if(this.$route.params.dataelementid) {
            promises.push(this.getDataElement(this.$route.params.dataelementid));
        }

        Promise.all(promises).then(() => {
            this.$store.commit("hideBlockingSpinner");
            this.filterDataReady = true;
        });
    }
}
</script>
